import { X } from 'lucide-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AISparkles from '@/components/common/AISparkles';
import SubmitButton from '@/components/common/SubmitButton';
import { Button } from '@/components/shadcn/button';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import type { CRM } from '@/features/crm/types';
import useBroker from '@/hooks/useBroker';
import type { DocumentExtractorPartyOneResult } from '@/types/singularity';

import AddressFields from './AddressFields';
import LegalPartyFields from './LegalPartyFields';
import NaturalPartyFields from './NaturalPartyFields';
import PartyTypeToggle from './PartyTypeToggle';
import { FIELDS_REQUIRED_BY_ANY_CRM } from './requiredFieldsByCrm';
import type { CreatePartyValues } from './types';

type Props = {
  extractedParty: DocumentExtractorPartyOneResult['party'];
  onClose: VoidFunction;
  onSubmit: (value: CreatePartyValues) => Promise<void>;
};

export default function CreatePartyForm({
  extractedParty,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  const { broker, crms: distributionCrms } = useBroker();

  const extractedAddress = extractedParty?.address;

  const form = useForm<CreatePartyValues>({
    resetOptions: {
      keepDirtyValues: true,
    },
    defaultValues: {
      entityType: extractedParty?.entity_type ?? 'NATURAL',
      email: extractedParty?.email ?? undefined,
      firstName: extractedParty?.first_name ?? undefined,
      lastName: extractedParty?.last_name ?? undefined,
      companyName: extractedParty?.company_name ?? undefined,
      initials: extractedParty?.initials ?? undefined,
      birthdate: extractedParty?.birth?.slice(0, 10),
      address: {
        street: extractedAddress?.street,
        housenr: extractedAddress?.housenr,
        boxnr: extractedAddress?.boxnr,
        zipcode: extractedAddress?.zipcode,
        city: extractedAddress?.city,
        country_code: extractedAddress?.country_code ?? broker?.country_code,
      },
      exportToCrm: Object.fromEntries(
        distributionCrms?.map((crm) => [crm.key as CRM, true]) ?? [],
      ),
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = form;

  const entityType = watch('entityType');

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative flex flex-col gap-4 p-4"
      >
        <Button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2"
          variant="ghost"
          size="icon-xs"
        >
          <X />
        </Button>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <p className="flex items-center gap-1 text-sm font-bold">
              <AISparkles />
              {t('PARTY_CREATE_INLINE_TITLE')}
            </p>
            <p className="text-muted-foreground text-sm">
              {t('PARTY_CREATE_INLINE_SUBTITLE')}
            </p>
          </div>

          <PartyTypeToggle />

          <Field data-invalid={errors.email ? true : undefined}>
            <FieldLabel htmlFor="email">
              {t('PARTY_CREATE_INLINE_EMAIL_LABEL')}
              {' *'}
            </FieldLabel>
            <Input
              id="email"
              {...register('email', {
                required: t('PARTY_CREATE_INLINE_REQUIRED'),
              })}
            />
            <FieldError errors={errors.email ? [errors.email] : undefined} />
          </Field>

          {entityType === 'NATURAL' && <NaturalPartyFields />}

          {entityType === 'LEGAL' && <LegalPartyFields />}

          <AddressFields />

          <div className="mt-2 flex flex-col">
            {distributionCrms?.map((crm) => (
              <Controller
                key={`exportToCrm.${crm.key as CRM}`}
                name={`exportToCrm.${crm.key as CRM}`}
                control={form.control}
                rules={{ deps: FIELDS_REQUIRED_BY_ANY_CRM }}
                render={({ field }) => (
                  <Field orientation="horizontal">
                    <Checkbox
                      id={field.name}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />

                    <FieldLabel htmlFor={field.name} className="cursor-pointer">
                      {t('PARTY_CREATE_INLINE_EXPORT_TO_CRM', {
                        crm: crm.name ?? crm.key,
                      })}
                    </FieldLabel>
                  </Field>
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="grow"
          >
            {t('PARTY_CREATE_INLINE_CANCEL')}
          </Button>

          <SubmitButton
            isSubmitting={isSubmitting}
            disabled={isSubmitting}
            className="grow"
          >
            {t('PARTY_CREATE_INLINE_SUBMIT')}
          </SubmitButton>
        </div>
      </form>
    </FormProvider>
  );
}
